import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  type IsActiveMatchOptions,
} from '@angular/router';
import { filter, map } from 'rxjs';

import { Icon } from '../icon/icon';
import { LayoutService } from './layout.service';
import { isNavGroup, navItemKey, type NavItem, type NavLink, type NavSection } from './nav-item';

/**
 * How a child link's route is matched against the active URL. `subset` on
 * paths keeps a parent link active on its own deeper routes; fragments and
 * matrix params are incidental to *which* page you are on, so they are
 * ignored rather than being allowed to defeat the match.
 */
const ROUTE_MATCH_OPTIONS: IsActiveMatchOptions = {
  paths: 'subset',
  queryParams: 'subset',
  fragment: 'ignored',
  matrixParams: 'ignored',
};

/**
 * Primary navigation for `<ui-app-shell>`. Renders the sections it is given —
 * the app owns the registry, this component owns the behaviour.
 *
 * Content slots (see projects/ui/README.md for the full guide):
 *  - `[uiSidenavBrandMark]` — compact mark, shown **only in the icon rail**,
 *    where it doubles as the expand control (rendered inside a button).
 *  - `[uiSidenavBrandName]` — full brand, shown **only when expanded**,
 *    rendered as a link to `brandRoute`. The two are alternatives the library
 *    swaps between, not a lockup — for an icon + wordmark lockup, put both
 *    in this slot.
 *  - `[uiSidenavFooter]` — anything below the footer links (theme toggle,
 *    user menu). Its wrapper carries `data-rail="true|false"` for apps that
 *    need rail-dependent styling from a global stylesheet.
 *
 * The brand wrappers are library-owned deliberately: projected content keeps
 * the app's style encapsulation, so an app cannot reliably hide part of it
 * from a rule scoped to this component's DOM.
 */
@Component({
  selector: 'ui-sidenav',
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, Icon],
  templateUrl: './sidenav.html',
  styleUrl: './sidenav.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Sidenav {
  /** Sidebar sections, in render order. */
  readonly sections = input<NavSection[]>([]);
  /** Links pinned to the bottom. Groups are not supported here. */
  readonly footerLinks = input<NavLink[]>([]);
  /** Accessible name for the <nav> landmark. */
  readonly ariaLabel = input<string>('Primary');
  /** Where the brand links to. */
  readonly brandRoute = input<string>('/');
  /** Accessible name for the brand link. */
  readonly brandLabel = input<string>('Home');

  protected readonly layout = inject(LayoutService);
  private readonly router = inject(Router);

  /** True when showing the icons-only rail (desktop collapsed state). */
  protected readonly rail = computed(() => this.layout.sidenavMode() === 'rail');

  /** Stable identity for rendering and expansion state — never the label alone. */
  protected readonly keyOf = navItemKey;

  /** Keys of the groups currently open. More than one may be open at a time. */
  private readonly expandedGroups = signal<ReadonlySet<string>>(new Set());

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  constructor() {
    // Opens whichever group owns the active route, so deep links and reloads
    // land expanded. This has to be reactive rather than a one-shot read in
    // the constructor: `sections` is an input, so it is still the empty
    // default while the constructor runs.
    effect(() => {
      // Read purely to re-run the effect on navigation — `Router.isActive`
      // reads router state directly, so the value itself is not needed. Do
      // not remove it as an unused read.
      this.currentUrl();

      const owning = this.sections()
        .flatMap((section) => section.items)
        .filter(
          (item) =>
            isNavGroup(item) &&
            item.children.some((c) => this.router.isActive(c.route, ROUTE_MATCH_OPTIONS)),
        )
        .map(navItemKey);

      if (!owning.length) {
        return;
      }

      // untracked: setExpanded reads expandedGroups, which would otherwise
      // make this effect depend on its own write.
      untracked(() => {
        for (const key of owning) {
          this.setExpanded(key, true);
        }
      });
    });
  }

  /**
   * The group's children, or `undefined` for a plain link. Templates narrow
   * with `@if (childrenOf(item); as children)` — Angular cannot narrow through
   * a type-guard call, but it does narrow an aliased truthy expression.
   */
  protected childrenOf(item: NavItem): NavLink[] | undefined {
    return isNavGroup(item) ? item.children : undefined;
  }

  protected isExpanded(item: NavItem): boolean {
    return this.expandedGroups().has(navItemKey(item));
  }

  protected toggleGroup(item: NavItem): void {
    // The icon rail has no room for child labels, so open the sidenav first
    // rather than expanding into a column the user cannot read.
    if (this.rail()) {
      this.layout.toggleSidenav();
      this.setExpanded(navItemKey(item), true);
      return;
    }

    this.setExpanded(navItemKey(item), !this.isExpanded(item));
  }

  private setExpanded(key: string, open: boolean): void {
    this.expandedGroups.update((current) => {
      if (current.has(key) === open) {
        return current;
      }
      const next = new Set(current);
      if (open) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }
}
